/**
 * EA Excel Parser
 *
 * Parses a bank EA taxonomy spreadsheet (.xlsx) containing up to 16,000 rows
 * across 10 sheets and builds a BulkEAImport object for canister submission.
 *
 * Sheet mapping:
 *   1. Organization Structure
 *   2. Business Capabilities (parsed but stored as metadata on organizations)
 *   3. Business Services
 *   4. HR Roles
 *   5. Process Flows (parsed but stored as metadata on business services)
 *   6. Process Steps (parsed but stored as metadata on business services)
 *   7. Applications
 *   8. Technical Services
 *   9. Infrastructure - skipped (handled by DeviceEmbedding)
 *  10. Data Objects
 *
 * Follows OOA/OOD principles with Single Responsibility:
 *   - EAExcelParser: orchestration and public API
 *   - SheetParser: individual sheet parsing (one method per sheet type)
 *   - RelationshipBuilder: extracts cross-entity relationships
 *   - IdGenerator: slugified ID creation
 */

import * as XLSX from 'xlsx';

import type {
    BulkEAImport,
    EAOrganizationRequest,
    EAApplicationRequest,
    EATechnicalServiceRequest,
    EABusinessServiceRequest,
    EADataObjectRequest,
    EAHRRoleRequest,
    EARelationshipRequest,
} from '../canister-client';

// ---------------------------------------------------------------------------
// Public Result Types
// ---------------------------------------------------------------------------

/** Summary counts returned alongside the parsed data. */
export interface SheetSummary {
    sheetName: string;
    rowsParsed: number;
}

/** Errors collected during parsing (non-fatal). */
export interface ParseError {
    sheet: string;
    row: number;
    message: string;
}

/** Top-level result returned by EAExcelParser.parse(). */
export interface ParseResult {
    data: BulkEAImport;
    summary: {
        sheetsFound: number;
        rowsParsedPerSheet: SheetSummary[];
        errors: ParseError[];
    };
}

// ---------------------------------------------------------------------------
// Internal Row Types (raw spreadsheet rows before mapping)
// ---------------------------------------------------------------------------

interface RawRow {
    [key: string]: string | number | undefined;
}

interface BusinessCapabilityRow {
    domain: string;
    capability: string;
    l2Capability: string;
    maturity: string;
    owner: string;
    keySystems: string;
    icon: string;
}

interface ProcessFlowRow {
    processName: string;
    bu: string;
    capability: string;
    platform: string;
    status: string;
    owner: string;
    trigger: string;
    sla: string;
    volume: string;
}

interface ProcessStepRow {
    flowId: string;
    stepNumber: number;
    type: string;
    role: string;
    system: string;
    infrastructure: string;
    location: string;
    input: string;
    output: string;
}

// ---------------------------------------------------------------------------
// IdGenerator - Slugified ID Creation
// ---------------------------------------------------------------------------

class IdGenerator {
    /**
     * Convert an arbitrary name string into a URL/ID-safe slug.
     *
     * Examples:
     *   "Retail Banking"        -> "retail-banking"
     *   "App: CRM / Salesforce" -> "app-crm-salesforce"
     *   "  Extra   Spaces  "    -> "extra-spaces"
     */
    static slugify(value: string): string {
        return value
            .toString()
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9\s-]/g, '')  // strip non-alphanumeric
            .replace(/[\s_]+/g, '-')        // spaces/underscores to hyphens
            .replace(/-+/g, '-')            // collapse consecutive hyphens
            .replace(/^-|-$/g, '');         // trim leading/trailing hyphens
    }

    /** Build an org ID from division + department + team. */
    static orgId(division: string, department: string, team: string): string {
        return `org-${IdGenerator.slugify(`${division}-${department}-${team}`)}`;
    }

    /** Build an application ID from the spreadsheet App ID column or name. */
    static appId(rawId: string, name: string): string {
        const base = rawId && rawId.trim().length > 0 ? rawId.trim() : name;
        return IdGenerator.slugify(base);
    }

    /** Build a technical service ID from the spreadsheet Service ID or name. */
    static techServiceId(rawId: string, name: string): string {
        const base = rawId && rawId.trim().length > 0 ? rawId.trim() : name;
        return IdGenerator.slugify(base);
    }

    /** Build a business service ID from domain + service name. */
    static businessServiceId(domain: string, serviceName: string): string {
        return `bs-${IdGenerator.slugify(`${domain}-${serviceName}`)}`;
    }

    /** Build a data object ID from the spreadsheet ID or name. */
    static dataObjectId(rawId: string, name: string): string {
        const base = rawId && rawId.trim().length > 0 ? rawId.trim() : name;
        return IdGenerator.slugify(base);
    }

    /** Build an HR role ID from division + department + role. */
    static hrRoleId(division: string, department: string, role: string): string {
        return `hr-${IdGenerator.slugify(`${division}-${department}-${role}`)}`;
    }
}

// ---------------------------------------------------------------------------
// SheetParser - Individual Sheet Parsing
// ---------------------------------------------------------------------------

/**
 * Responsible for reading raw rows from each sheet and mapping them
 * into the corresponding request types. Each public method handles
 * exactly one sheet (Single Responsibility).
 */
class SheetParser {
    private errors: ParseError[] = [];

    getErrors(): ParseError[] {
        return this.errors;
    }

    // -- helpers --

    private cellStr(row: RawRow, key: string): string {
        const val = row[key];
        if (val === undefined || val === null) return '';
        return String(val).trim();
    }

    private cellNum(row: RawRow, key: string): number {
        const val = row[key];
        if (val === undefined || val === null) return 0;
        const num = Number(val);
        return isNaN(num) ? 0 : num;
    }

    /**
     * Find the best matching column header key from the raw row.
     * Spreadsheet headers may have inconsistent casing or extra whitespace.
     */
    private findKey(row: RawRow, candidates: string[]): string {
        const rowKeys = Object.keys(row);
        for (const candidate of candidates) {
            // exact match first
            if (rowKeys.includes(candidate)) return candidate;
            // case-insensitive match
            const lower = candidate.toLowerCase();
            const match = rowKeys.find(k => k.toLowerCase() === lower);
            if (match) return match;
        }
        return candidates[0];
    }

    // -- Sheet 1: Organization Structure --

    parseOrganizations(rows: RawRow[], sheetName: string): EAOrganizationRequest[] {
        const results: EAOrganizationRequest[] = [];
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            try {
                const division = this.cellStr(r, this.findKey(r, ['Division']));
                const department = this.cellStr(r, this.findKey(r, ['Department']));
                const team = this.cellStr(r, this.findKey(r, ['Team']));

                if (!division && !department && !team) continue; // skip blank rows

                const org: EAOrganizationRequest = {
                    org_id: IdGenerator.orgId(division, department, team),
                    division,
                    department,
                    team,
                    headcount: this.cellNum(r, this.findKey(r, ['Headcount'])),
                    location_type: this.cellStr(r, this.findKey(r, ['Location Type', 'LocationType', 'Location'])),
                    reports_to: this.cellStr(r, this.findKey(r, ['Reports To', 'ReportsTo'])),
                    key_responsibilities: this.cellStr(r, this.findKey(r, ['Key Responsibilities', 'KeyResponsibilities'])),
                    metadata: [],
                };
                results.push(org);
            } catch (err) {
                this.errors.push({ sheet: sheetName, row: i + 2, message: String(err) });
            }
        }
        return results;
    }

    // -- Sheet 2: Business Capabilities (metadata-enrichment only) --

    parseBusinessCapabilities(rows: RawRow[], sheetName: string): BusinessCapabilityRow[] {
        const results: BusinessCapabilityRow[] = [];
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            try {
                const domain = this.cellStr(r, this.findKey(r, ['Domain']));
                const capability = this.cellStr(r, this.findKey(r, ['Capability', 'Capability L1']));
                if (!domain && !capability) continue;

                results.push({
                    domain,
                    capability,
                    l2Capability: this.cellStr(r, this.findKey(r, ['L2 Capability', 'L2Capability', 'Capability L2'])),
                    maturity: this.cellStr(r, this.findKey(r, ['Maturity'])),
                    owner: this.cellStr(r, this.findKey(r, ['Owner'])),
                    keySystems: this.cellStr(r, this.findKey(r, ['Key Systems', 'KeySystems'])),
                    icon: this.cellStr(r, this.findKey(r, ['Icon'])),
                });
            } catch (err) {
                this.errors.push({ sheet: sheetName, row: i + 2, message: String(err) });
            }
        }
        return results;
    }

    // -- Sheet 3: Business Services --

    parseBusinessServices(rows: RawRow[], sheetName: string): EABusinessServiceRequest[] {
        const results: EABusinessServiceRequest[] = [];
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            try {
                const serviceDomain = this.cellStr(r, this.findKey(r, ['Service Domain', 'ServiceDomain']));
                const businessService = this.cellStr(r, this.findKey(r, ['Business Service', 'BusinessService']));
                if (!serviceDomain && !businessService) continue;

                const svc: EABusinessServiceRequest = {
                    service_id: IdGenerator.businessServiceId(serviceDomain, businessService),
                    service_domain: serviceDomain,
                    business_service: businessService,
                    service_offering: this.cellStr(r, this.findKey(r, ['Service Offering', 'ServiceOffering'])),
                    channel: this.cellStr(r, this.findKey(r, ['Channel'])),
                    sla: this.cellStr(r, this.findKey(r, ['SLA'])),
                    service_owner: this.cellStr(r, this.findKey(r, ['Service Owner', 'ServiceOwner'])),
                    technical_service: this.cellStr(r, this.findKey(r, ['Technical Service', 'TechnicalService'])),
                    metadata: [],
                };
                results.push(svc);
            } catch (err) {
                this.errors.push({ sheet: sheetName, row: i + 2, message: String(err) });
            }
        }
        return results;
    }

    // -- Sheet 4: HR Roles --

    parseHRRoles(rows: RawRow[], sheetName: string): EAHRRoleRequest[] {
        const results: EAHRRoleRequest[] = [];
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            try {
                const division = this.cellStr(r, this.findKey(r, ['Division']));
                const department = this.cellStr(r, this.findKey(r, ['Department']));
                const role = this.cellStr(r, this.findKey(r, ['Role']));
                if (!division && !department && !role) continue;

                const hr: EAHRRoleRequest = {
                    role_id: IdGenerator.hrRoleId(division, department, role),
                    division,
                    department,
                    role,
                    grade: this.cellStr(r, this.findKey(r, ['Grade'])),
                    headcount: this.cellNum(r, this.findKey(r, ['Headcount'])),
                    location: this.cellStr(r, this.findKey(r, ['Location'])),
                    key_skills: this.cellStr(r, this.findKey(r, ['Key Skills', 'KeySkills'])),
                    systems_used: this.cellStr(r, this.findKey(r, ['Systems Used', 'SystemsUsed'])),
                    metadata: [],
                };
                results.push(hr);
            } catch (err) {
                this.errors.push({ sheet: sheetName, row: i + 2, message: String(err) });
            }
        }
        return results;
    }

    // -- Sheet 5: Process Flows (metadata-enrichment only) --

    parseProcessFlows(rows: RawRow[], sheetName: string): ProcessFlowRow[] {
        const results: ProcessFlowRow[] = [];
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            try {
                const processName = this.cellStr(r, this.findKey(r, ['Process Name', 'ProcessName']));
                if (!processName) continue;

                results.push({
                    processName,
                    bu: this.cellStr(r, this.findKey(r, ['BU', 'Process Domain', 'ProcessDomain'])),
                    capability: this.cellStr(r, this.findKey(r, ['Capability'])),
                    platform: this.cellStr(r, this.findKey(r, ['Platform'])),
                    status: this.cellStr(r, this.findKey(r, ['Status'])),
                    owner: this.cellStr(r, this.findKey(r, ['Owner'])),
                    trigger: this.cellStr(r, this.findKey(r, ['Trigger'])),
                    sla: this.cellStr(r, this.findKey(r, ['SLA'])),
                    volume: this.cellStr(r, this.findKey(r, ['Volume', 'Volume/Day'])),
                });
            } catch (err) {
                this.errors.push({ sheet: sheetName, row: i + 2, message: String(err) });
            }
        }
        return results;
    }

    // -- Sheet 6: Process Steps (metadata-enrichment only) --

    parseProcessSteps(rows: RawRow[], sheetName: string): ProcessStepRow[] {
        const results: ProcessStepRow[] = [];
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            try {
                const flowId = this.cellStr(r, this.findKey(r, ['Flow ID', 'FlowID', 'Process ID', 'ProcessID']));
                if (!flowId) continue;

                results.push({
                    flowId,
                    stepNumber: this.cellNum(r, this.findKey(r, ['Step Number', 'StepNumber', 'Step #', 'Step'])),
                    type: this.cellStr(r, this.findKey(r, ['Type', 'Step Name', 'StepName'])),
                    role: this.cellStr(r, this.findKey(r, ['Role', 'Swim Lane', 'Swim Lane (Role)'])),
                    system: this.cellStr(r, this.findKey(r, ['System'])),
                    infrastructure: this.cellStr(r, this.findKey(r, ['Infrastructure', 'Integration'])),
                    location: this.cellStr(r, this.findKey(r, ['Location'])),
                    input: this.cellStr(r, this.findKey(r, ['Input'])),
                    output: this.cellStr(r, this.findKey(r, ['Output'])),
                });
            } catch (err) {
                this.errors.push({ sheet: sheetName, row: i + 2, message: String(err) });
            }
        }
        return results;
    }

    // -- Sheet 7: Applications --

    parseApplications(rows: RawRow[], sheetName: string): EAApplicationRequest[] {
        const results: EAApplicationRequest[] = [];
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            try {
                const rawId = this.cellStr(r, this.findKey(r, ['App ID', 'AppID', 'Application ID', 'ApplicationID']));
                const name = this.cellStr(r, this.findKey(r, ['Name', 'Application Name', 'ApplicationName', 'App Name']));
                if (!rawId && !name) continue;

                const integratesWith = this.cellStr(r, this.findKey(r, ['Integrates With', 'IntegratesWith']));
                const integratesWithList = integratesWith
                    ? integratesWith.split(/[,;]/).map(s => s.trim()).filter(Boolean)
                    : [];

                const app: EAApplicationRequest = {
                    app_id: IdGenerator.appId(rawId, name),
                    name,
                    vendor: this.cellStr(r, this.findKey(r, ['Vendor'])),
                    app_type: this.cellStr(r, this.findKey(r, ['Type'])),
                    domain: this.cellStr(r, this.findKey(r, ['Domain'])),
                    description: this.cellStr(r, this.findKey(r, ['Description'])),
                    users: this.cellNum(r, this.findKey(r, ['Users'])),
                    criticality: this.cellStr(r, this.findKey(r, ['Criticality'])),
                    integration_method: this.cellStr(r, this.findKey(r, ['Integration Method', 'IntegrationMethod'])),
                    integrates_with: integratesWithList.length > 0 ? [integratesWithList] : [],
                    metadata: [],
                };
                results.push(app);
            } catch (err) {
                this.errors.push({ sheet: sheetName, row: i + 2, message: String(err) });
            }
        }
        return results;
    }

    // -- Sheet 8: Technical Services --

    parseTechnicalServices(rows: RawRow[], sheetName: string): EATechnicalServiceRequest[] {
        const results: EATechnicalServiceRequest[] = [];
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            try {
                const rawId = this.cellStr(r, this.findKey(r, ['Service ID', 'ServiceID', 'Technical Service ID', 'TechnicalServiceID']));
                const name = this.cellStr(r, this.findKey(r, ['Name', 'Technical Service Name', 'TechnicalServiceName', 'Service Name']));
                if (!rawId && !name) continue;

                const ts: EATechnicalServiceRequest = {
                    service_id: IdGenerator.techServiceId(rawId, name),
                    name,
                    application: this.cellStr(r, this.findKey(r, ['Application'])),
                    service_type: this.cellStr(r, this.findKey(r, ['Type'])),
                    api_interface: this.cellStr(r, this.findKey(r, ['API/Interface', 'APIInterface', 'API Interface'])),
                    description: this.cellStr(r, this.findKey(r, ['Description'])),
                    availability_sla: this.cellStr(r, this.findKey(r, ['Availability SLA', 'AvailabilitySLA'])),
                    owner: this.cellStr(r, this.findKey(r, ['Owner'])),
                    metadata: [],
                };
                results.push(ts);
            } catch (err) {
                this.errors.push({ sheet: sheetName, row: i + 2, message: String(err) });
            }
        }
        return results;
    }

    // -- Sheet 9: Infrastructure (imported as technical services with infra prefix) --

    parseInfrastructure(rows: RawRow[], sheetName: string): EATechnicalServiceRequest[] {
        const results: EATechnicalServiceRequest[] = [];
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            try {
                const rawId = this.cellStr(r, this.findKey(r, ['CI ID', 'CIID', 'Device ID', 'DeviceID', 'Infrastructure ID']));
                const name = this.cellStr(r, this.findKey(r, ['CI Name', 'CIName', 'Name', 'Device Name']));
                if (!rawId && !name) continue;

                const deviceType = this.cellStr(r, this.findKey(r, ['Type', 'Device Type', 'CI Type']));
                const environment = this.cellStr(r, this.findKey(r, ['Environment', 'Env']));
                const location = this.cellStr(r, this.findKey(r, ['Location', 'Data Center', 'Region']));
                const supportsApps = this.cellStr(r, this.findKey(r, ['Supports Applications', 'Applications', 'Supported Applications']));
                const specification = this.cellStr(r, this.findKey(r, ['Specification', 'Spec', 'Specs']));
                const owner = this.cellStr(r, this.findKey(r, ['Owner']));
                const criticality = this.cellStr(r, this.findKey(r, ['Criticality', 'Priority']));

                const metadata: Array<[string, string]> = [
                    ['_source', 'infrastructure'],
                    ['device_type', deviceType],
                    ['environment', environment],
                    ['location', location],
                    ['supports_applications', supportsApps],
                    ['specification', specification],
                    ['criticality', criticality],
                ].filter(([, v]) => v !== '') as Array<[string, string]>;

                const ts: EATechnicalServiceRequest = {
                    service_id: rawId ? `infra-${rawId.toLowerCase().replace(/\s+/g, '-')}` : `infra-${name.toLowerCase().replace(/\s+/g, '-')}`,
                    name,
                    application: supportsApps,
                    service_type: `infrastructure:${deviceType}`,
                    api_interface: '',
                    description: `${deviceType} — ${environment} — ${location}`,
                    availability_sla: criticality,
                    owner,
                    metadata,
                };
                results.push(ts);
            } catch (err) {
                this.errors.push({ sheet: sheetName, row: i + 2, message: String(err) });
            }
        }
        return results;
    }

    // -- Sheet 10: Data Objects --

    parseDataObjects(rows: RawRow[], sheetName: string): EADataObjectRequest[] {
        const results: EADataObjectRequest[] = [];
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            try {
                const rawId = this.cellStr(r, this.findKey(r, ['Data Object ID', 'DataObjectID']));
                const name = this.cellStr(r, this.findKey(r, ['Name', 'Data Object', 'DataObject', 'Data Object Name']));
                if (!rawId && !name) continue;

                const obj: EADataObjectRequest = {
                    data_object_id: IdGenerator.dataObjectId(rawId, name),
                    name,
                    domain: this.cellStr(r, this.findKey(r, ['Domain'])),
                    description: this.cellStr(r, this.findKey(r, ['Description'])),
                    source_system: this.cellStr(r, this.findKey(r, ['Source System', 'SourceSystem'])),
                    owner: this.cellStr(r, this.findKey(r, ['Owner'])),
                    classification: this.cellStr(r, this.findKey(r, ['Classification'])),
                    retention: this.cellStr(r, this.findKey(r, ['Retention'])),
                    key_attributes: this.cellStr(r, this.findKey(r, ['Key Attributes', 'KeyAttributes'])),
                    metadata: [],
                };
                results.push(obj);
            } catch (err) {
                this.errors.push({ sheet: sheetName, row: i + 2, message: String(err) });
            }
        }
        return results;
    }
}

// ---------------------------------------------------------------------------
// RelationshipBuilder - Cross-Entity Relationship Extraction
// ---------------------------------------------------------------------------

/**
 * Builds EARelationshipRequest records from the parsed entity data.
 *
 * Relationship sources:
 *   - Sheet 7 "Integrates With"   -> application <-> application
 *   - Sheet 3 "Technical Service" -> business_service <-> technical_service
 *   - Sheet 8 "Application"       -> technical_service <-> application
 *   - Sheet 4 "Systems Used"      -> hr_role <-> application
 */
class RelationshipBuilder {
    private relationships: EARelationshipRequest[] = [];

    /** Index of known application IDs for lookup by name or raw ID. */
    private appIndex: Map<string, string> = new Map();

    /** Index of known technical service IDs for lookup by name or raw ID. */
    private techServiceIndex: Map<string, string> = new Map();

    /**
     * Populate internal indices so relationship targets can be resolved by
     * name or raw spreadsheet ID.
     */
    buildIndices(
        applications: EAApplicationRequest[],
        technicalServices: EATechnicalServiceRequest[],
    ): void {
        for (const app of applications) {
            this.appIndex.set(app.name.toLowerCase(), app.app_id);
            this.appIndex.set(app.app_id.toLowerCase(), app.app_id);
        }
        for (const ts of technicalServices) {
            this.techServiceIndex.set(ts.name.toLowerCase(), ts.service_id);
            this.techServiceIndex.set(ts.service_id.toLowerCase(), ts.service_id);
        }
    }

    /** Resolve an application reference to its canonical ID, falling back to slugify. */
    private resolveAppId(ref: string): string {
        const key = ref.toLowerCase().trim();
        return this.appIndex.get(key) ?? IdGenerator.slugify(ref);
    }

    /** Resolve a technical service reference to its canonical ID, falling back to slugify. */
    private resolveTechServiceId(ref: string): string {
        const key = ref.toLowerCase().trim();
        return this.techServiceIndex.get(key) ?? IdGenerator.slugify(ref);
    }

    /** Sheet 7: application integrates_with -> application<->application relationships. */
    buildApplicationIntegrations(applications: EAApplicationRequest[]): void {
        for (const app of applications) {
            // integrates_with is CandidOpt<string[]>: [] = None, [string[]] = Some
            const targets = app.integrates_with.length > 0 ? app.integrates_with[0] as string[] : [];
            if (targets.length === 0) continue;

            for (const targetRef of targets) {
                this.relationships.push({
                    source_type: 'application',
                    source_id: app.app_id,
                    target_type: 'application',
                    target_id: this.resolveAppId(targetRef),
                    relationship_type: 'integrates_with',
                    relationship_id: [],
                    metadata: [],
                });
            }
        }
    }

    /** Sheet 3: business_service -> technical_service relationships. */
    buildBusinessServiceLinks(businessServices: EABusinessServiceRequest[]): void {
        for (const bs of businessServices) {
            if (!bs.technical_service) continue;

            // Technical service field may contain multiple comma-separated references
            const refs = bs.technical_service.split(/[,;]/).map(s => s.trim()).filter(Boolean);
            for (const ref of refs) {
                this.relationships.push({
                    source_type: 'business_service',
                    source_id: bs.service_id,
                    target_type: 'technical_service',
                    target_id: this.resolveTechServiceId(ref),
                    relationship_type: 'supported_by',
                    relationship_id: [],
                    metadata: [],
                });
            }
        }
    }

    /** Sheet 8: technical_service -> application relationships. */
    buildTechnicalServiceAppLinks(technicalServices: EATechnicalServiceRequest[]): void {
        for (const ts of technicalServices) {
            if (!ts.application) continue;

            // Application field may contain multiple comma-separated references
            const refs = ts.application.split(/[,;]/).map(s => s.trim()).filter(Boolean);
            for (const ref of refs) {
                this.relationships.push({
                    source_type: 'technical_service',
                    source_id: ts.service_id,
                    target_type: 'application',
                    target_id: this.resolveAppId(ref),
                    relationship_type: 'hosted_on',
                    relationship_id: [],
                    metadata: [],
                });
            }
        }
    }

    /** Sheet 4: hr_role systems_used -> hr_role<->application relationships. */
    buildHRRoleSystemLinks(hrRoles: EAHRRoleRequest[]): void {
        for (const hr of hrRoles) {
            if (!hr.systems_used) continue;

            const refs = hr.systems_used.split(/[,;]/).map(s => s.trim()).filter(Boolean);
            for (const ref of refs) {
                this.relationships.push({
                    source_type: 'hr_role',
                    source_id: hr.role_id,
                    target_type: 'application',
                    target_id: this.resolveAppId(ref),
                    relationship_type: 'uses',
                    relationship_id: [],
                    metadata: [],
                });
            }
        }
    }

    /** Return all accumulated relationships. */
    getRelationships(): EARelationshipRequest[] {
        return this.relationships;
    }
}

// ---------------------------------------------------------------------------
// Sheet Name Matching
// ---------------------------------------------------------------------------

/**
 * Known sheet name patterns (case-insensitive substring match).
 * Ordered from most-specific to least-specific to avoid false matches.
 * The categorizeSheet function tries patterns in iteration order, so
 * more-specific categories (e.g. "technical service") should appear
 * before broader ones (e.g. "services").
 */
const SHEET_PATTERNS: Array<[string, string[]]> = [
    // Most specific first
    ['technicalServices', ['technical service', 'tech service', 'tech_service']],
    ['businessServices', ['business service', 'biz service']],
    ['processSteps', ['process step', 'process_step', 'process connector', 'process_connector']],
    ['processFlows', ['process flow', 'process_flow', 'business process']],
    ['dataObjects', ['data object', 'data_object', 'data entities', 'data model']],
    ['hrRoles', ['hr role', 'hr roles', 'hr_role', 'human resource']],
    ['organization', ['organization', 'org structure', 'org_structure', 'departments', 'divisions']],
    ['capabilities', ['capabilit', 'capability']],
    ['applications', ['application', 'app portfolio', 'app_portfolio']],
    ['infrastructure', ['infrastructure', 'infra']],
];

/**
 * Match a workbook sheet name to a known category.
 * Returns the category key or null if no match.
 * Tries more-specific patterns first to avoid false matches.
 */
function categorizeSheet(sheetName: string): string | null {
    const lower = sheetName.toLowerCase();
    for (const [category, patterns] of SHEET_PATTERNS) {
        if (patterns.some(p => lower.includes(p))) {
            return category;
        }
    }
    return null;
}

// ---------------------------------------------------------------------------
// EAExcelParser - Public API
// ---------------------------------------------------------------------------

/**
 * Orchestrates the parsing of a bank EA taxonomy spreadsheet.
 *
 * Usage:
 * ```typescript
 * const parser = new EAExcelParser();
 * const result = parser.parse(arrayBuffer);
 * // result.data is a BulkEAImport ready for canister call
 * // result.summary has counts and any non-fatal errors
 * ```
 */
export class EAExcelParser {
    private sheetParser: SheetParser;
    private relationshipBuilder: RelationshipBuilder;

    constructor() {
        this.sheetParser = new SheetParser();
        this.relationshipBuilder = new RelationshipBuilder();
    }

    /**
     * Parse an .xlsx file from an ArrayBuffer and produce a BulkEAImport.
     *
     * @param buffer - The raw file bytes (e.g. from FileReader.readAsArrayBuffer)
     * @returns ParseResult containing the import data and a parsing summary
     */
    parse(buffer: ArrayBuffer): ParseResult {
        const workbook = XLSX.read(buffer, { type: 'array' });

        // Categorize available sheets
        const sheetMap = new Map<string, string>(); // category -> actual sheet name
        const unmatchedSheets: string[] = [];
        for (const name of workbook.SheetNames) {
            const category = categorizeSheet(name);
            if (category) {
                sheetMap.set(category, name);
            } else {
                unmatchedSheets.push(name);
            }
        }

        console.log('[EA Parser] Workbook sheets:', workbook.SheetNames);
        console.log('[EA Parser] Matched categories:', Object.fromEntries(sheetMap));
        if (unmatchedSheets.length > 0) {
            console.warn('[EA Parser] Unmatched sheets:', unmatchedSheets);
        }

        const summaryRows: SheetSummary[] = [];

        // Helper: read rows from a categorized sheet
        const readRows = (category: string): RawRow[] => {
            const sheetName = sheetMap.get(category);
            if (!sheetName) return [];
            const sheet = workbook.Sheets[sheetName];
            if (!sheet) return [];
            return XLSX.utils.sheet_to_json<RawRow>(sheet, { defval: '' });
        };

        const trackSummary = (category: string, count: number): void => {
            const sheetName = sheetMap.get(category);
            if (sheetName) {
                summaryRows.push({ sheetName, rowsParsed: count });
            }
        };

        // -- Parse each sheet --

        // Sheet 1: Organization Structure
        const orgRows = readRows('organization');
        const organizations = this.sheetParser.parseOrganizations(
            orgRows, sheetMap.get('organization') ?? 'Organization Structure',
        );
        trackSummary('organization', organizations.length);

        // Sheet 2: Business Capabilities (enrichment data, not a direct import type)
        const capRows = readRows('capabilities');
        const capabilities = this.sheetParser.parseBusinessCapabilities(
            capRows, sheetMap.get('capabilities') ?? 'Business Capabilities',
        );
        trackSummary('capabilities', capabilities.length);

        // Enrich organizations with capability metadata
        this.enrichOrganizationsWithCapabilities(organizations, capabilities);

        // Sheet 3: Business Services
        const bsRows = readRows('businessServices');
        const businessServices = this.sheetParser.parseBusinessServices(
            bsRows, sheetMap.get('businessServices') ?? 'Business Services',
        );
        trackSummary('businessServices', businessServices.length);

        // Sheet 4: HR Roles
        const hrRows = readRows('hrRoles');
        const hrRoles = this.sheetParser.parseHRRoles(
            hrRows, sheetMap.get('hrRoles') ?? 'HR Roles',
        );
        trackSummary('hrRoles', hrRoles.length);

        // Sheet 5: Process Flows (enrichment data)
        const pfRows = readRows('processFlows');
        const processFlows = this.sheetParser.parseProcessFlows(
            pfRows, sheetMap.get('processFlows') ?? 'Process Flows',
        );
        trackSummary('processFlows', processFlows.length);

        // Sheet 6: Process Steps (enrichment data)
        const psRows = readRows('processSteps');
        const processSteps = this.sheetParser.parseProcessSteps(
            psRows, sheetMap.get('processSteps') ?? 'Process Steps',
        );
        trackSummary('processSteps', processSteps.length);

        // Enrich business services with process metadata
        this.enrichBusinessServicesWithProcesses(businessServices, processFlows, processSteps);

        // Sheet 7: Applications
        const appRows = readRows('applications');
        if (appRows.length > 0) {
            console.log('[EA Parser] Applications sheet columns:', Object.keys(appRows[0]));
        }
        const applications = this.sheetParser.parseApplications(
            appRows, sheetMap.get('applications') ?? 'Applications',
        );
        console.log(`[EA Parser] Applications: ${appRows.length} rows → ${applications.length} parsed`);
        trackSummary('applications', applications.length);

        // Sheet 8: Technical Services
        const tsRows = readRows('technicalServices');
        if (tsRows.length > 0) {
            console.log('[EA Parser] Technical Services sheet columns:', Object.keys(tsRows[0]));
        }
        const technicalServices = this.sheetParser.parseTechnicalServices(
            tsRows, sheetMap.get('technicalServices') ?? 'Technical Services',
        );
        console.log(`[EA Parser] Technical Services: ${tsRows.length} rows → ${technicalServices.length} parsed`);
        trackSummary('technicalServices', technicalServices.length);

        // Sheet 9: Infrastructure — parse as technical services with infra prefix
        const infraRows = readRows('infrastructure');
        if (infraRows.length > 0) {
            console.log('[EA Parser] Infrastructure sheet columns:', Object.keys(infraRows[0]));
        }
        const infraItems = this.sheetParser.parseInfrastructure(
            infraRows, sheetMap.get('infrastructure') ?? 'Infrastructure',
        );
        console.log(`[EA Parser] Infrastructure: ${infraRows.length} rows → ${infraItems.length} parsed`);
        trackSummary('infrastructure', infraItems.length);
        // Merge infra items into technical services
        technicalServices.push(...infraItems);

        // Sheet 10: Data Objects
        const doRows = readRows('dataObjects');
        if (doRows.length > 0) {
            console.log('[EA Parser] Data Objects sheet columns:', Object.keys(doRows[0]));
        }
        const dataObjects = this.sheetParser.parseDataObjects(
            doRows, sheetMap.get('dataObjects') ?? 'Data Objects',
        );
        console.log(`[EA Parser] Data Objects: ${doRows.length} rows → ${dataObjects.length} parsed`);
        trackSummary('dataObjects', dataObjects.length);

        // -- Build relationships --

        this.relationshipBuilder.buildIndices(applications, technicalServices);
        this.relationshipBuilder.buildApplicationIntegrations(applications);
        this.relationshipBuilder.buildBusinessServiceLinks(businessServices);
        this.relationshipBuilder.buildTechnicalServiceAppLinks(technicalServices);
        this.relationshipBuilder.buildHRRoleSystemLinks(hrRoles);

        const relationships = this.relationshipBuilder.getRelationships();

        // -- Assemble result --

        const data: BulkEAImport = {
            organizations,
            applications,
            technical_services: technicalServices,
            business_services: businessServices,
            data_objects: dataObjects,
            hr_roles: hrRoles,
            relationships,
        };

        return {
            data,
            summary: {
                sheetsFound: sheetMap.size,
                rowsParsedPerSheet: summaryRows,
                errors: this.sheetParser.getErrors(),
            },
        };
    }

    // -- Enrichment helpers --

    /**
     * Attach business capability metadata to matching organizations.
     * Capabilities are matched to organizations by domain (loosely mapped to division).
     */
    private enrichOrganizationsWithCapabilities(
        organizations: EAOrganizationRequest[],
        capabilities: BusinessCapabilityRow[],
    ): void {
        if (capabilities.length === 0) return;

        // Build a lookup: lowercase division -> organization
        const orgByDivision = new Map<string, EAOrganizationRequest[]>();
        for (const org of organizations) {
            const key = org.division.toLowerCase();
            const existing = orgByDivision.get(key) ?? [];
            existing.push(org);
            orgByDivision.set(key, existing);
        }

        for (const cap of capabilities) {
            const matchingOrgs = orgByDivision.get(cap.domain.toLowerCase());
            if (!matchingOrgs) continue;

            for (const org of matchingOrgs) {
                // metadata is CandidOpt: [] = None, [tuples] = Some(tuples)
                const tuples: Array<[string, string]> = org.metadata.length > 0
                    ? (org.metadata[0] as Array<[string, string]>)
                    : [];
                tuples.push(['capability_domain', cap.domain]);
                tuples.push(['capability', cap.capability]);
                if (cap.l2Capability) tuples.push(['l2_capability', cap.l2Capability]);
                if (cap.maturity) tuples.push(['capability_maturity', cap.maturity]);
                if (cap.icon) tuples.push(['capability_icon', cap.icon]);
                org.metadata = [tuples];
            }
        }
    }

    /**
     * Attach process flow and step metadata to matching business services.
     * Process flows are matched by capability or platform to business service domain.
     */
    private enrichBusinessServicesWithProcesses(
        businessServices: EABusinessServiceRequest[],
        processFlows: ProcessFlowRow[],
        processSteps: ProcessStepRow[],
    ): void {
        if (processFlows.length === 0) return;

        // Build step lookup by flow ID
        const stepsByFlowId = new Map<string, ProcessStepRow[]>();
        for (const step of processSteps) {
            const existing = stepsByFlowId.get(step.flowId) ?? [];
            existing.push(step);
            stepsByFlowId.set(step.flowId, existing);
        }

        // Match process flows to business services by capability or platform
        const bsByDomain = new Map<string, EABusinessServiceRequest[]>();
        for (const bs of businessServices) {
            const key = bs.service_domain.toLowerCase();
            const existing = bsByDomain.get(key) ?? [];
            existing.push(bs);
            bsByDomain.set(key, existing);
        }

        for (const flow of processFlows) {
            // Try matching by BU or capability to service domain
            const candidates = bsByDomain.get(flow.bu.toLowerCase())
                ?? bsByDomain.get(flow.capability.toLowerCase())
                ?? [];

            for (const bs of candidates) {
                // metadata is CandidOpt: [] = None, [tuples] = Some(tuples)
                const tuples: Array<[string, string]> = bs.metadata.length > 0
                    ? (bs.metadata[0] as Array<[string, string]>)
                    : [];
                tuples.push(['process_name', flow.processName]);
                if (flow.status) tuples.push(['process_status', flow.status]);
                if (flow.trigger) tuples.push(['process_trigger', flow.trigger]);
                if (flow.sla) tuples.push(['process_sla', flow.sla]);
                if (flow.volume) tuples.push(['process_volume', flow.volume]);

                // Attach step count for the flow
                const flowSlug = IdGenerator.slugify(flow.processName);
                const steps = stepsByFlowId.get(flowSlug) ?? stepsByFlowId.get(flow.processName) ?? [];
                if (steps.length > 0) {
                    tuples.push(['process_step_count', String(steps.length)]);
                }

                bs.metadata = [tuples];
            }
        }
    }
}

// Re-export the slugify helper for external use (e.g. testing or custom ID generation)
export const slugify = IdGenerator.slugify;
